import React from 'react';
import { useApolloClient } from '@apollo/client';
import { sortBy } from 'lodash/fp';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useAuth, useUser } from '../utils/hooks/auth';
import { useVenueSwitchContext } from '../utils/hooks/useVenueSwitchContext';
import { keyById } from '../utils';
import { Select, Typography, TypographyProps } from '../ui-kit';
import { useSilentMutation } from '../graphql/hooks';
import { SWITCH_VENUE } from '../graphql/mutations';
import { useGetInitInfoQuery } from '../generated/graphql';
import { filterByIds } from './utils';

const VenueName = ({ currentVenue }: { currentVenue: string}) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

    let variant: undefined | TypographyProps['variant'];

    if (!isDesktop) {
        variant = 'body2';
    }

    return (
        <Typography align='center' variant={variant}>
            {currentVenue}
        </Typography>
    );
};

const AccountControlsMenu = () => {
    const { user } = useUser();
    const { venueIds, currentVenueId } = user;
    const [selectedId, setSelectedId] = React.useState(currentVenueId);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { isVenueSwitching, setIsVenueSwitching } = useVenueSwitchContext();

    const [switchVenue] = useSilentMutation(SWITCH_VENUE);
    const { login: loginUser } = useAuth();

    const client = useApolloClient();

    const { data } = useGetInitInfoQuery({ fetchPolicy: 'cache-only' });
    const venues = data ? data.venues : [];
    const currentUserVenues = filterByIds(venues, venueIds);
    const sortedVenues = sortBy(['name', 'asc'], currentUserVenues);
    const keyedVenues = keyById(currentUserVenues);


    const selectedVenue = keyedVenues[selectedId] ? keyedVenues[selectedId].name : '';

    const handleVenueSwitch = async (venueId: string) => {
        setIsVenueSwitching(true);
        const { data: res } = await switchVenue({ variables: { venueId } });
        setIsVenueSwitching(false);
        const { switchVenue: token } = res;
        loginUser({ token });
        await client.clearStore();
    };

    const handleMenuItemClick = (id: string) => {
        if (id === selectedId) {
            return;
        }
        setSelectedId(id);
        // eslint-disable-next-line no-void
        void handleVenueSwitch(id);
    };

    const singleVenue = venueIds.length === 1;

    if (singleVenue) {
        return <VenueName currentVenue={selectedVenue} />;
    }

    return (
        <Select
            handleMenuItemClick={handleMenuItemClick}
            menuItems={sortedVenues}
            isDisabled={isVenueSwitching}
            selectedId={currentVenueId}
            selectedName={selectedVenue}
            reduceFontOnMobile
        />
    );
};

export default AccountControlsMenu;
